import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { StaggerItem, Card, Button, Label, SectionDivider } from "../components/Primitives";
import { ReadinessGauge, StreakFlame } from "../components/Celebration";
import { RadialProgress, MacroRing } from "../components/Charts";
import { useStaggeredReveal } from "../utils/hooks";
import { useWorkouts, useMealPlan, useDailyTracking, useDashboard, transformWorkouts, useAnnouncements } from "../hooks/useApi";
import { announcementsApi, meals as mealsApi } from "../api/client";
import storage from "../utils/storage";
import { tapLight, tapMedium, tapDouble } from "../utils/haptics";
import { useAuth } from "../context/AuthContext";

export default function TodayView({ C, onWork, onNav, showToast }) {
  const { user } = useAuth();
  const activeSessionCheck = storage.get("active_session", null);
  const hasRecovery = activeSessionCheck && activeSessionCheck.dayNum && Date.now() - activeSessionCheck.timestamp < 86400000;
  const visible = useStaggeredReveal(hasRecovery ? 17 : 16, 55);
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const now = new Date();
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const dayNames = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
  const dateStr = `${dayNames[now.getDay()]} — ${months[now.getMonth()]} ${now.getDate()}, ${now.getFullYear()}`;
  const todayKey = now.toISOString().split("T")[0]; // YYYY-MM-DD, locale-safe

  // ─── API data ───
  const { announcements: trainerAnnouncements, refresh: refreshAnnouncements } = useAnnouncements();
  const { workouts: apiWorkouts, loading: workoutsLoading } = useWorkouts();
  const transformedWorkouts = transformWorkouts(apiWorkouts);
  const { meals: MEALS, macroCaps: MACRO_CAPS_API, supplements: SUPPLEMENTS, loading: mealsLoading } = useMealPlan();
  const { waterGlasses: apiWater, supplementChecks: apiSuppChecks, mealChecks: apiMealChecks, update: updateTracking, loading: trackingLoading } = useDailyTracking();
  const { data: dashData } = useDashboard();

  const MACRO_CAPS = MACRO_CAPS_API || { cal: 0, p: 0, c: 0, f: 0 };

  const hasWorkouts = transformedWorkouts.length > 0;
  const cycleLen = transformedWorkouts.length || 1;
  const [currentDay, setCurrentDay] = useState(() => storage.get("cd", 1));

  // Clamp currentDay when workouts load/change
  useEffect(() => {
    if (hasWorkouts && currentDay > cycleLen) {
      setCurrentDay(1);
      storage.set("cd", 1);
    }
  }, [hasWorkouts, cycleLen]);

  const dayData = transformedWorkouts.find(d => d.d === currentDay) || transformedWorkouts[0] || null;
  const isRest = dayData ? !!dayData.rest : true;

  // Secondary-content disclosure. Default collapsed — users reach for primary
  // actions (workout, nutrition, hydration, supplements) first.
  const [showMore, setShowMore] = useState(false);

  // Snap & Estimate state
  const [snapAnalyzing, setSnapAnalyzing] = useState(false);
  const [snapResult, setSnapResult] = useState(null); // { calories, proteinG, carbsG, fatG, description, ... }

  const [mealsChecked, setMealsChecked] = useState(() => storage.get("mc_" + todayKey, {}));
  const mealsCompleted = Object.values(mealsChecked).filter(Boolean).length;
  const [expandedMeal, setExpandedMeal] = useState(null);
  const [foodLogs, setFoodLogs] = useState(() => storage.get("fl_" + todayKey, {}));
  const [editingFood, setEditingFood] = useState(null);
  const [mealPhotos, setMealPhotos] = useState(() => storage.get("mp_" + todayKey, {}));
  const mealFileRefs = useRef({});
  const toggleMeal = async (i) => {
    tapLight();
    const turningOn = !mealsChecked[i];
    const next = { ...mealsChecked, [i]: turningOn };
    setMealsChecked(next);
    storage.set("mc_" + todayKey, next);
    if (turningOn) {
      const mealName = MEALS?.[i]?.name || "Meal";
      const nowDone = Object.values(next).filter(Boolean).length;
      const total = MEALS?.length || 0;
      showToast?.(nowDone === total && total > 0 ? "All meals logged — nice work." : `${mealName} logged`);
    }
    try {
      await updateTracking({ mealChecks: JSON.stringify(next) });
    } catch (err) {
      console.error("[Forge] meal toggle failed", err);
      showToast?.("Failed to save meal status. Check connection.");
    }
  };

  const handleMealPhoto = (mealIdx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const reader = new FileReader();
      reader.onerror = () => {
        console.error("[Forge] meal photo read failed");
        showToast?.("Failed to read photo. Try again.");
      };
      reader.onload = (ev) => {
        try {
          const next = { ...mealPhotos, [mealIdx]: { photo: ev.target.result, time: Date.now() } };
          setMealPhotos(next);
          storage.set("mp_" + todayKey, next);
          showToast?.("Meal photo logged");
        } catch (err) {
          console.error("[Forge] meal photo save failed", err);
          showToast?.("Failed to save photo. Storage may be full.");
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error("[Forge] meal photo failed", err);
      showToast?.("Failed to process photo.");
    }
  };

  const [waterCount, setWaterCount] = useState(() => storage.get("wt_" + todayKey, 0));
  const waterGoal = dashData?.waterGoal || 16;

  const [suppChecked, setSuppChecked] = useState(() => storage.get("sp_" + todayKey, {}));
  const toggleSupp = async (i) => {
    tapLight();
    const turningOn = !suppChecked[i];
    const next = { ...suppChecked, [i]: turningOn };
    setSuppChecked(next);
    storage.set("sp_" + todayKey, next);
    if (turningOn) {
      const nowDone = Object.values(next).filter(Boolean).length;
      const total = SUPPLEMENTS?.length || 0;
      showToast?.(nowDone === total && total > 0 ? "All supplements taken." : `Logged — ${SUPPLEMENTS?.[i] || "supplement"}`);
    }
    try {
      await updateTracking({ supplementChecks: JSON.stringify(next) });
    } catch (err) {
      console.error("[Forge] supplement toggle failed", err);
      showToast?.("Failed to save supplement status. Check connection.");
    }
  };

  const changeDay = useCallback((dir) => {
    tapDouble();
    const next = dir === "prev"
      ? (currentDay > 1 ? currentDay - 1 : cycleLen)
      : (currentDay < cycleLen ? currentDay + 1 : 1);
    setCurrentDay(next);
    storage.set("cd", next);
  }, [currentDay, cycleLen]);

  const [activeSession] = useState(() => {
    const s = storage.get("active_session", null);
    if (s && s.dayNum && Date.now() - s.timestamp < 86400000) return s;
    if (s) storage.remove("active_session");
    return null;
  });

  // Sync tracking state from API
  useEffect(() => {
    if (!trackingLoading && apiWater != null) setWaterCount(apiWater);
  }, [trackingLoading, apiWater]);

  useEffect(() => {
    if (!trackingLoading && apiSuppChecks) setSuppChecked(apiSuppChecks);
  }, [trackingLoading, apiSuppChecks]);

  useEffect(() => {
    if (!trackingLoading && apiMealChecks) setMealsChecked(apiMealChecks);
  }, [trackingLoading, apiMealChecks]);

  const stats = { workoutCount: dashData?.activity?.workoutsThisWeek || 0, streak: 0, checkInCount: 0, totalVolumeAllTime: 0 };
  const readiness = dashData?.readiness || null;

  // Sum actual macros from completed meals (memoized to avoid recalc on every render)
  const { macroP, macroC, macroF, consumedCal, totalCal } = useMemo(() => {
    const completed = MEALS.filter((_, i) => mealsChecked[i]);
    return {
      macroP: completed.reduce((sum, m) => sum + m.p, 0),
      macroC: completed.reduce((sum, m) => sum + m.c, 0),
      macroF: completed.reduce((sum, m) => sum + m.f, 0),
      consumedCal: completed.reduce((sum, m) => sum + m.cal, 0),
      totalCal: MACRO_CAPS.cal,
    };
  }, [MEALS, mealsChecked, MACRO_CAPS]);

  const suppTotal = SUPPLEMENTS.length;
  const suppDone = Object.values(suppChecked).filter(Boolean).length;
  const so = activeSession ? 1 : 0; // stagger offset for recovery card

  return (
    <div>
      {/* ─── HERO ─── */}
      <StaggerItem index={0} visible={visible}>
        <div style={{
          margin: "-18px -16px 0", padding: "28px 24px 24px",
          background: `linear-gradient(180deg, ${C.accent005} 0%, transparent 100%)`,
          position: "relative", overflow: "hidden",
        }}>
          <div style={{ position: "absolute", top: -140, right: -120, width: 360, height: 360, borderRadius: "50%", background: `radial-gradient(circle, ${C.accent005} 0%, transparent 70%)` }} />
          <div style={{ position: "absolute", bottom: -80, left: -60, width: 240, height: 240, borderRadius: "50%", background: `radial-gradient(circle, ${C.secondary005} 0%, transparent 70%)` }} />
          {/* Bottom edge line */}
          <div style={{ position: "absolute", bottom: 0, left: "5%", right: "5%", height: 1, background: C.dividerGrad, opacity: 0.3 }} />
          <div style={{ position: "relative" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)", letterSpacing: ".22em", marginBottom: 16, opacity: 0.7 }}>
                  {dateStr}
                </div>
                <div style={{ fontSize: 'var(--fs-display)', fontWeight: 800, color: C.text1, lineHeight: 1.1, fontFamily: "var(--d)" }}>
                  {greeting},
                </div>
                <div style={{
                  fontSize: 'var(--fs-display)', fontWeight: 800, lineHeight: 1.15, fontFamily: "var(--d)", marginTop: 4,
                  background: C.gradient, backgroundSize: "300% 100%",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
                  animation: "goldShimmer 12s ease-in-out infinite",
                }}>
                  {user?.firstName || "Athlete"}.
                </div>
              </div>
              {stats.streak > 0 && <StreakFlame streak={stats.streak} C={C} />}
            </div>
          </div>
        </div>
      </StaggerItem>

      {/* ─── TRAINER ANNOUNCEMENTS ─── */}
      {trainerAnnouncements.length > 0 && trainerAnnouncements.map((ann) => (
        <StaggerItem key={ann.id} index={1} visible={visible}>
          <Card C={C} style={{ borderLeft: `3px solid ${C.accent}`, padding: "16px 20px", marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--gap-md)", marginBottom: 8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round">
                <path d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "var(--fs-xs)", color: C.accent, fontFamily: "var(--m)", letterSpacing: ".06em", marginBottom: 4 }}>
                  FROM {(ann.trainerName || "YOUR TRAINER").toUpperCase()}
                </div>
                <div style={{ fontSize: "var(--fs-base)", color: C.text1, fontFamily: "var(--b)", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                  {ann.message}
                </div>
              </div>
              <button
                onClick={() => {
                  announcementsApi.markRead(ann.id).then(refreshAnnouncements);
                }}
                style={{ background: "none", border: "none", color: C.text4, cursor: "pointer", padding: 4, flexShrink: 0 }}
                aria-label="Dismiss"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </Card>
        </StaggerItem>
      ))}

      {/* ─── SESSION RECOVERY (urgent — always first after hero) ─── */}
      {activeSession && (
        <StaggerItem index={1} visible={visible}>
          <Card C={C} style={{ borderLeft: `3px solid ${C.warn}`, padding: "16px 20px", marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 'var(--gap-md)', marginBottom: 10 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.warn} strokeWidth="2" strokeLinecap="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <div>
                <div style={{ fontSize: 'var(--fs-md)', fontWeight: 700, color: C.text1 }}>Interrupted Workout</div>
                <div style={{ fontSize: 'var(--fs-xs)', color: C.text4, fontFamily: "var(--m)", marginTop: 2 }}>
                  Day {activeSession.dayNum} · Exercise {activeSession.exerciseIndex + 1}, Set {activeSession.setIndex + 1}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 'var(--sp-sm)' }}>
              <Button C={C} onClick={() => {
                const resumeDay = transformedWorkouts[activeSession.dayNum - 1];
                if (resumeDay) onWork(resumeDay);
              }} style={{ flex: 1, fontSize: 'var(--fs-sm)' }}>RESUME WORKOUT</Button>
              <button onClick={() => {
                storage.remove("active_session");
                window.location.reload();
              }} style={{
                padding: "10px 16px", background: "transparent",
                border: `1.5px solid ${C.structBorderHover}`, borderRadius: 10,
                color: C.text4, fontSize: 'var(--fs-sm)', fontWeight: 600, fontFamily: "var(--m)",
                cursor: "pointer", letterSpacing: ".06em",
              }}>DISMISS</button>
            </div>
          </Card>
        </StaggerItem>
      )}

      {/* ─── DAY SELECTOR ─── */}
      {(hasWorkouts || workoutsLoading) && (
        <StaggerItem index={2 + so} visible={visible}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 'var(--sp-lg)', margin: "6px 0 10px" }}>
            <button onClick={() => changeDay("prev")} aria-label="Previous day" style={{
              minWidth: 72, height: 44, padding: "0 12px", borderRadius: 12,
              background: C.structGlass,
              border: `1.5px solid ${C.structBorderHover}`, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              color: C.text3, transition: "all 0.2s",
              fontFamily: "var(--m)", fontSize: "var(--fs-2xs)",
              letterSpacing: ".14em", textTransform: "uppercase",
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M15 18l-6-6 6-6" /></svg>
              <span>Prev</span>
            </button>
            <div style={{ textAlign: "center", minWidth: 120 }}>
              <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: C.accent, fontFamily: "var(--m)", letterSpacing: ".16em" }}>
                DAY {currentDay} OF {cycleLen}
              </div>
              <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: C.text1, fontFamily: "var(--d)", marginTop: 2 }}>
                {workoutsLoading ? "Loading..." : isRest ? "Rest & Recovery" : dayData?.t || "Workout"}
              </div>
            </div>
            <button onClick={() => changeDay("next")} aria-label="Next day" style={{
              minWidth: 72, height: 44, padding: "0 12px", borderRadius: 12,
              background: C.structGlass,
              border: `1.5px solid ${C.structBorderHover}`, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              color: C.text3, transition: "all 0.2s",
              fontFamily: "var(--m)", fontSize: "var(--fs-2xs)",
              letterSpacing: ".14em", textTransform: "uppercase",
            }}>
              <span>Next</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 18l6-6-6-6" /></svg>
            </button>
          </div>
        </StaggerItem>
      )}

      {/* ─── WORKOUT CARD ─── */}
      <StaggerItem index={3 + so} visible={visible}>
        {workoutsLoading ? (
          <Card C={C} style={{ padding: 'var(--sp-2xl)' }}>
            <div style={{ height: 20, width: "60%", borderRadius: 6, background: C.structBorderHover, marginBottom: 12, animation: "pulse 1.5s ease-in-out infinite" }} />
            <div style={{ height: 14, width: "40%", borderRadius: 4, background: C.structBorderHover, animation: "pulse 1.5s ease-in-out infinite" }} />
          </Card>
        ) : !dayData ? (
          <Card C={C} style={{ borderLeft: `3px solid ${C.text4}`, padding: 'var(--sp-2xl)' }}>
            <div style={{ fontSize: 'var(--fs-title)', fontWeight: 700, color: C.text2, fontFamily: "var(--d)", marginBottom: 8 }}>No Workouts Assigned</div>
            <div style={{ fontSize: 'var(--fs-base)', color: C.text4, fontFamily: "var(--m)", lineHeight: 1.7 }}>
              Your trainer hasn&apos;t assigned any workouts yet. Check back soon or reach out to your coach.
            </div>
          </Card>
        ) : isRest ? (
          <Card C={C} style={{ borderLeft: `3px solid ${C.text4}`, padding: 'var(--sp-2xl)' }}>
            <div style={{ display: "flex", alignItems: "center", gap: 'var(--gap-md)', marginBottom: 8 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.text4} strokeWidth="1.5" strokeLinecap="round">
                <path d="M18 20V10" /><path d="M12 20V4" /><path d="M6 20v-6" />
              </svg>
              <div style={{ fontSize: 'var(--fs-title)', fontWeight: 700, color: C.text2, fontFamily: "var(--d)" }}>Rest Day</div>
            </div>
            <div style={{ fontSize: 'var(--fs-base)', color: C.text4, fontFamily: "var(--m)", marginTop: 2, lineHeight: 1.7 }}>
              Growth happens during recovery, not in the gym. Light walking or stretching only. Your body is building right now.
            </div>
          </Card>
        ) : (
          <Card C={C} style={{ borderLeft: `3px solid ${C.accent}`, padding: 'var(--sp-2xl)' }} accentGlow>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: C.accent, fontFamily: "var(--m)", letterSpacing: ".16em", marginBottom: 6 }}>TODAY&apos;S WORKOUT</div>
                <div style={{ fontSize: 'var(--fs-display)', fontWeight: 700, color: C.text1, fontFamily: "var(--d)", lineHeight: 1.1 }}>{dayData.t}</div>
                <div style={{ fontSize: 'var(--fs-base)', color: C.text3, fontFamily: "var(--m)", marginTop: 6 }}>{dayData.x?.length || 0} exercises · {dayData.m} min</div>
              </div>
              {readiness && (
                <ReadinessGauge score={readiness.score} label={readiness.label} color={readiness.color} C={C} />
              )}
            </div>
            <Button C={C} onClick={() => onWork(dayData)} style={{ marginTop: 18 }}>START WORKOUT</Button>
          </Card>
        )}
      </StaggerItem>

      {/* ─── QUICK ACTIONS (tucked behind MORE) ─── */}
      {showMore && (
      <StaggerItem index={5 + so} visible={visible}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 'var(--sp-sm)', margin: "14px 0" }}>
          {[
            { l: "Check-In", d: "Body tracking", v: "ci", tone: C.secondary, icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.secondary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
              </svg>
            )},
            { l: "Volume Log", d: "Session tonnage", v: "vl", tone: C.accent, icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="1.5" strokeLinecap="round">
                <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
              </svg>
            )},
            { l: "Analytics", d: "Track progress", v: "data", tone: C.accent, icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4,18 9,13 13,15 20,6" /><polyline points="16,6 20,6 20,10" />
              </svg>
            )},
            { l: "Guide", d: "Training info", v: "gd", tone: C.accent, icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" /><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
              </svg>
            )},
          ].map(({ l, d, v, icon, tone }) => (
            <Card key={v} C={C} onClick={() => onNav?.(v)} style={{
              padding: "14px 10px", cursor: "pointer", marginBottom: 0,
              display: "flex", flexDirection: "column", alignItems: "center",
              textAlign: "center", gap: 'var(--gap-sm)',
              borderTop: `2px solid ${tone}20`,
            }}>
              <div style={{
                width: 34, height: 34, borderRadius: 10,
                background: `${tone}08`, border: `1px solid ${tone}18`,
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: `0 0 12px ${tone}10`,
              }}>
                {icon}
              </div>
              <div>
                <div style={{ fontSize: 'var(--fs-body)', fontWeight: 600, color: C.text1 }}>{l}</div>
                <div style={{ fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)", marginTop: 2 }}>{d}</div>
              </div>
            </Card>
          ))}
        </div>
      </StaggerItem>
      )}

      <SectionDivider C={C} />

      {/* ─── NUTRITION ─── */}
      <StaggerItem index={6 + so} visible={visible}>
        <Label C={C}>Nutrition</Label>
        {mealsLoading ? (
          <Card C={C} style={{ padding: 'var(--card-pad-lg)' }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", background: C.structBorderHover, animation: "pulse 1.5s ease-in-out infinite" }} />
              <div style={{ display: "flex", gap: 'var(--sp-md)' }}>
                {[1, 2, 3].map(k => (
                  <div key={k} style={{ width: 52, height: 52, borderRadius: "50%", background: C.structBorderHover, animation: "pulse 1.5s ease-in-out infinite" }} />
                ))}
              </div>
            </div>
            {[1, 2, 3].map(k => (
              <div key={k} style={{ height: 14, width: `${70 - k * 10}%`, borderRadius: 4, background: C.structBorderHover, marginBottom: 10, animation: "pulse 1.5s ease-in-out infinite" }} />
            ))}
          </Card>
        ) : (
          <Card C={C} style={{ padding: 'var(--card-pad-lg)' }}>
            {/* Meal Snap & Estimate — promoted above the ring; it's the novel action */}
            <div style={{ marginBottom: 16 }}>
              <input ref={el => { if (el) mealFileRefs.current.snap = el; }} type="file" accept="image/*" capture="environment"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = async (ev) => {
                    const dataUrl = ev.target.result || "";
                    const base64 = dataUrl.split(",")[1] || "";
                    const mime = (dataUrl.match(/^data:(image\/[a-z0-9+.-]+);/i) || [])[1] || file.type || "image/jpeg";
                    // Save to localStorage for offline history
                    const snap = { photo: dataUrl, time: Date.now(), status: "analyzing" };
                    const snaps = storage.get("meal_snaps_" + todayKey, []);
                    snaps.push(snap);
                    storage.set("meal_snaps_" + todayKey, snaps);
                    // Call Claude Vision for macro estimation
                    setSnapAnalyzing(true);
                    setSnapResult(null);
                    tapMedium();
                    try {
                      const analysis = await mealsApi.analyzeFoodPhoto(base64, mime);
                      setSnapResult({ ...analysis, photo: dataUrl });
                      showToast?.(`~${analysis.calories} cal · ${analysis.proteinG}g P · ${analysis.carbsG}g C · ${analysis.fatG}g F`);
                    } catch (err) {
                      console.error("[Forge] food photo analysis failed", err);
                      showToast?.(`Analysis failed: ${err.message || "try again"}`);
                    } finally {
                      setSnapAnalyzing(false);
                    }
                  };
                  reader.readAsDataURL(file);
                  e.target.value = "";
                }}
                style={{ display: "none" }}
              />
              <button onClick={() => mealFileRefs.current.snap?.click()} style={{
                width: "100%", padding: "12px 16px",
                background: `linear-gradient(135deg, ${C.accent005}, ${C.secondary005})`,
                border: `1.5px solid ${C.accent020}`,
                borderRadius: 10, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 'var(--gap-md)',
                transition: "all 0.2s",
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: C.accent010, border: `1px solid ${C.accent020}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" /><circle cx="12" cy="13" r="4" />
                  </svg>
                </div>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 'var(--fs-body)', fontWeight: 700, color: C.text1, fontFamily: "var(--m)", letterSpacing: ".04em" }}>SNAP &amp; ESTIMATE</div>
                  <div style={{ fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)", marginTop: 1 }}>Photo your meal for instant macro analysis</div>
                </div>
                <div style={{ marginLeft: "auto", padding: "3px 8px", borderRadius: 4, background: C.accent010, border: `1px solid ${C.accent015}` }}>
                  <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 700, color: C.accent, fontFamily: "var(--m)", letterSpacing: ".06em" }}>PRO</div>
                </div>
              </button>

              {snapAnalyzing && (
                <div style={{
                  marginTop: 10, padding: "10px 14px", borderRadius: 10,
                  background: C.structGlass, border: `1px dashed ${C.accent020}`,
                  display: "flex", alignItems: "center", gap: 10,
                  animation: "pulse 1.2s ease-in-out infinite",
                }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.accent, animation: "typingDot 1.4s ease-in-out infinite" }} />
                  <div style={{ fontSize: 'var(--fs-xs)', color: C.text3, fontFamily: "var(--m)", letterSpacing: ".1em", textTransform: "uppercase" }}>
                    Analyzing photo…
                  </div>
                </div>
              )}

              {snapResult && !snapAnalyzing && (
                <div style={{
                  marginTop: 10, padding: "12px 14px", borderRadius: 10,
                  background: `linear-gradient(135deg, ${C.accent010}, ${C.secondary010})`,
                  border: `1px solid ${C.accent030}`,
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <div style={{ fontSize: 'var(--fs-xs)', color: C.accent, fontFamily: "var(--m)", letterSpacing: ".14em", fontWeight: 700 }}>ESTIMATE</div>
                    <button onClick={() => { setSnapResult(null); tapLight(); }} style={{
                      background: "none", border: "none", color: C.text4, cursor: "pointer",
                      fontSize: 'var(--fs-2xs)', fontFamily: "var(--m)", letterSpacing: ".1em",
                    }}>DISMISS</button>
                  </div>
                  <div style={{ fontSize: 'var(--fs-md)', color: C.text1, marginBottom: 8, lineHeight: 1.4 }}>
                    {snapResult.description || "Food photo analyzed"}
                  </div>
                  <div style={{ display: "flex", gap: 'var(--sp-md)', marginBottom: 10 }}>
                    {[
                      { l: "CAL", v: snapResult.calories || "—", color: C.text1 },
                      { l: "P", v: `${snapResult.proteinG || 0}g`, color: C.accent },
                      { l: "C", v: `${snapResult.carbsG || 0}g`, color: C.secondary },
                      { l: "F", v: `${snapResult.fatG || 0}g`, color: C.accentDeep },
                    ].map(({ l, v, color }) => (
                      <div key={l} style={{ flex: 1, textAlign: "center" }}>
                        <div style={{ fontSize: 'var(--fs-h3)', fontWeight: 700, color, fontFamily: "var(--m)" }}>{v}</div>
                        <div style={{ fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)", letterSpacing: ".1em" }}>{l}</div>
                      </div>
                    ))}
                  </div>
                  {snapResult.caloriesLow != null && snapResult.caloriesHigh != null && (
                    <div style={{ fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)" }}>
                      Range: {snapResult.caloriesLow}–{snapResult.caloriesHigh} cal · {snapResult.confidence || "estimate"}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Calorie Ring + Macros — context after the primary action */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <RadialProgress value={consumedCal} max={totalCal} C={C} size={72} label="KCAL" sublabel={`${consumedCal}/${totalCal}`} />
              <div style={{ display: "flex", gap: 'var(--sp-md)' }}>
                <MacroRing consumed={macroP} target={MACRO_CAPS.p} color={C.accent} label="PROTEIN" C={C} size={52} />
                <MacroRing consumed={macroC} target={MACRO_CAPS.c} color={C.secondary} label="CARBS" C={C} size={52} />
                <MacroRing consumed={macroF} target={MACRO_CAPS.f} color={C.accentDeep} label="FAT" C={C} size={52} />
              </div>
            </div>

            {/* Meals List */}
            {MEALS.length === 0 ? (
              <div style={{ fontSize: 'var(--fs-body)', color: C.text4, fontFamily: "var(--m)", textAlign: "center", padding: "12px 0" }}>
                No meal plan assigned yet.
              </div>
            ) : MEALS.map((meal, i) => (
              <div key={i}>
                <div style={{ display: "flex", alignItems: "center", gap: 'var(--gap-md)', padding: "10px 0", borderBottom: i < MEALS.length - 1 ? `1px solid ${C.structBorder}` : "none", cursor: "pointer" }}>
                  <div
                    role="checkbox" aria-checked={!!mealsChecked[i]} tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); toggleMeal(i); }}
                    onKeyDown={e => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), e.stopPropagation(), toggleMeal(i))}
                    style={{ width: 24, height: 24, borderRadius: 7, border: `1.5px solid ${mealsChecked[i] ? C.ok : C.structBorderHover}`, background: mealsChecked[i] ? `${C.ok}20` : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.2s" }}>
                    {mealsChecked[i] && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.ok} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                    )}
                  </div>
                  <div role="button" tabIndex={0} onClick={() => setExpandedMeal(expandedMeal === i ? null : i)} onKeyDown={e => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setExpandedMeal(expandedMeal === i ? null : i))} style={{ flex: 1, cursor: "pointer" }}>
                    <div style={{ fontSize: 'var(--fs-md)', color: mealsChecked[i] ? C.text3 : C.text1, textDecoration: mealsChecked[i] ? "line-through" : "none", transition: "color 0.2s" }}>{meal.n}</div>
                  </div>
                  {mealPhotos[i] && (
                    <div style={{ width: 20, height: 20, borderRadius: 4, overflow: "hidden", flexShrink: 0, border: `1px solid ${C.secondary}` }}>
                      <img src={mealPhotos[i].photo} alt={`${meal.n} photo`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  )}
                  <div style={{ fontSize: 'var(--fs-xs)', color: C.text4, fontFamily: "var(--m)" }}>{meal.p}g P · {meal.cal} cal</div>
                  <div role="button" tabIndex={0} onClick={() => setExpandedMeal(expandedMeal === i ? null : i)} onKeyDown={e => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setExpandedMeal(expandedMeal === i ? null : i))} style={{ color: C.text4, fontSize: 'var(--fs-base)', transform: expandedMeal === i ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s", cursor: "pointer", padding: 4 }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 9l6 6 6-6" /></svg>
                  </div>
                </div>

                {/* Expanded Meal Detail */}
                {expandedMeal === i && (
                  <div style={{ padding: "10px 0 10px 34px", borderBottom: i < MEALS.length - 1 ? `1px solid ${C.structBorder}` : "none" }}>
                    {/* Meal description and notes from trainer */}
                    {meal.desc && (
                      <div style={{ fontSize: 'var(--fs-sm)', color: "rgba(255,255,255,0.5)", fontFamily: "var(--m)", lineHeight: 1.5, marginBottom: 8 }}>
                        {meal.desc}
                      </div>
                    )}
                    {meal.notes && (
                      <div style={{ fontSize: 'var(--fs-sm)', color: "rgba(255,255,255,0.45)", fontFamily: "var(--m)", fontStyle: "italic", lineHeight: 1.5, marginBottom: 8, paddingLeft: 8, borderLeft: `2px solid ${C.structBorderHover}` }}>
                        {meal.notes}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 'var(--sp-sm)', marginBottom: 10 }}>
                      {[
                        { l: "CAL", v: meal.cal, color: C.text1 },
                        { l: "PROTEIN", v: `${meal.p}g`, color: C.accent },
                        { l: "CARBS", v: `${meal.c}g`, color: C.secondary },
                        { l: "FAT", v: `${meal.f}g`, color: C.text1 },
                      ].map(({ l, v, color }) => (
                        <div key={l} style={{ flex: 1, textAlign: "center", padding: "6px 4px", background: C.structGlass, borderRadius: 6, border: `1px solid ${C.structBorder}` }}>
                          <div style={{ fontSize: 'var(--fs-base)', fontWeight: 600, color, fontFamily: "var(--m)" }}>{v}</div>
                          <div style={{ fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)", letterSpacing: ".1em", marginTop: 2 }}>{l}</div>
                        </div>
                      ))}
                    </div>
                    <input
                      ref={el => mealFileRefs.current[i] = el}
                      type="file" accept="image/*" capture="environment"
                      onChange={e => handleMealPhoto(i, e)}
                      style={{ display: "none" }}
                    />
                    <div style={{ display: "flex", gap: 'var(--gap-sm)' }}>
                      <button onClick={() => mealFileRefs.current[i]?.click()} style={{
                        flex: 1, padding: "8px 10px", background: mealPhotos[i] ? C.secondary010 : C.structGlass,
                        border: `1px solid ${mealPhotos[i] ? C.secondary : C.structBorderHover}`, borderRadius: 6,
                        color: mealPhotos[i] ? C.secondary : C.text3, fontSize: 'var(--fs-xs)', fontFamily: "var(--m)",
                        cursor: "pointer", letterSpacing: ".08em", display: "flex", alignItems: "center",
                        justifyContent: "center", gap: 'var(--gap-sm)', minHeight: 36, transition: "all 0.2s",
                      }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" /><circle cx="12" cy="13" r="4" /></svg>
                        {mealPhotos[i] ? "PHOTO LOGGED" : "SNAP & LOG"}
                      </button>
                    </div>
                    {mealPhotos[i] && (
                      <div style={{ marginTop: 8, borderRadius: 8, overflow: "hidden" }}>
                        <img src={mealPhotos[i].photo} alt="meal" style={{ width: "100%", borderRadius: 8, display: "block", maxHeight: 160, objectFit: "cover" }} />
                      </div>
                    )}

                    {/* Food Log — what client actually ate */}
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.structBorder}` }}>
                      {foodLogs[i] ? (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div>
                            <div style={{ fontSize: 'var(--fs-sm)', color: C.text3, fontFamily: "var(--m)" }}>
                              {foodLogs[i].desc || "Followed plan"}
                              {foodLogs[i].adherence === false && <span style={{ color: C.warn, marginLeft: 6, fontSize: 'var(--fs-2xs)', letterSpacing: ".06em" }}>OFF PLAN</span>}
                            </div>
                            {foodLogs[i].p && (
                              <div style={{ fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)", marginTop: 2 }}>
                                {foodLogs[i].p}p · {foodLogs[i].c}c · {foodLogs[i].f}f · {foodLogs[i].cal}cal
                              </div>
                            )}
                          </div>
                          <button onClick={() => setEditingFood(i)} style={{
                            background: "none", border: "none", color: C.text4, cursor: "pointer", padding: 4,
                          }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => setEditingFood(i)} style={{
                          width: "100%", padding: "8px 10px", background: C.structGlass,
                          border: `1px dashed ${C.structBorderHover}`, borderRadius: 6,
                          color: C.text4, fontSize: 'var(--fs-xs)', fontFamily: "var(--m)",
                          cursor: "pointer", letterSpacing: ".06em",
                        }}>LOG WHAT I ATE</button>
                      )}
                      {editingFood === i && (
                        <div style={{ marginTop: 8, padding: 10, background: C.structGlass, borderRadius: 8, border: `1px solid ${C.structBorderHover}` }}>
                          <input
                            placeholder="What did you eat?"
                            defaultValue={foodLogs[i]?.desc || ""}
                            id={`food-desc-${i}`}
                            style={{ width: "100%", padding: "8px 10px", background: "transparent", border: `1px solid ${C.structBorderHover}`, borderRadius: 6, color: C.text1, fontSize: 'var(--fs-base)', fontFamily: "var(--b)", outline: "none", marginBottom: 8 }}
                          />
                          <div style={{ display: "flex", gap: 'var(--gap-sm)', marginBottom: 8 }}>
                            {["p", "c", "f", "cal"].map(k => (
                              <input key={k} placeholder={k === "cal" ? "cal" : k.toUpperCase()} defaultValue={foodLogs[i]?.[k] || ""}
                                id={`food-${k}-${i}`} type="number" inputMode="decimal"
                                style={{ flex: 1, padding: "6px 4px", background: "transparent", border: `1px solid ${C.structBorderHover}`, borderRadius: 4, color: C.text1, fontSize: 'var(--fs-body)', fontFamily: "var(--m)", textAlign: "center", outline: "none" }}
                              />
                            ))}
                          </div>
                          <div style={{ display: "flex", gap: 'var(--gap-sm)' }}>
                            <button onClick={() => {
                              const desc = document.getElementById(`food-desc-${i}`)?.value || "";
                              const p = document.getElementById(`food-p-${i}`)?.value || "";
                              const c = document.getElementById(`food-c-${i}`)?.value || "";
                              const f = document.getElementById(`food-f-${i}`)?.value || "";
                              const cal = document.getElementById(`food-cal-${i}`)?.value || "";
                              const entry = { desc: desc || "Followed plan", adherence: true, p, c, f, cal, time: Date.now() };
                              const next = { ...foodLogs, [i]: entry };
                              setFoodLogs(next);
                              storage.set("fl_" + todayKey, next);
                              setEditingFood(null);
                              if (!mealsChecked[i]) toggleMeal(i);
                            }} style={{
                              flex: 1, padding: "8px 10px", background: C.gradientBtn, backgroundSize: "300% 100%",
                              border: "none", borderRadius: 6, color: C.btnText, fontSize: 'var(--fs-xs)', fontWeight: 700,
                              fontFamily: "var(--m)", cursor: "pointer", letterSpacing: ".06em",
                            }}>SAVE</button>
                            <button onClick={() => setEditingFood(null)} style={{
                              padding: "8px 12px", background: "transparent",
                              border: `1px solid ${C.structBorderHover}`, borderRadius: 6,
                              color: C.text4, fontSize: 'var(--fs-xs)', fontFamily: "var(--m)", cursor: "pointer",
                            }}>CANCEL</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </Card>
        )}
      </StaggerItem>

      {/* ─── CARDIO (tucked behind MORE) ─── */}
      {showMore && (
      <StaggerItem index={7 + so} visible={visible}>
        {(() => {
          const cardioToday = storage.get("cardio_" + todayKey, { sessions: [], steps: 0 });
          const cardioMins = (cardioToday.sessions || []).reduce((a, s) => a + (s.duration || 0), 0);
          const cardioSteps = cardioToday.steps || 0;
          return (
            <Card C={C} onClick={() => onNav?.("cardio")} style={{ padding: 'var(--card-pad)', cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: cardioMins > 0 || cardioSteps > 0 ? 12 : 0 }}>
                <Label C={C} style={{ marginBottom: 0 }}>Cardio & Steps</Label>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.text4} strokeWidth="2" strokeLinecap="round"><path d="M9 18l6-6-6-6" /></svg>
              </div>
              {cardioMins > 0 || cardioSteps > 0 ? (
                <div style={{ display: "flex", gap: 'var(--sp-lg)' }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 'var(--fs-h2)', fontWeight: 700, color: C.accent, fontFamily: "var(--m)" }}>{cardioMins}</div>
                    <div style={{ fontSize: 'var(--fs-micro)', color: C.text4, fontFamily: "var(--m)", letterSpacing: ".12em" }}>MIN</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 'var(--fs-h2)', fontWeight: 700, color: C.secondary, fontFamily: "var(--m)" }}>{cardioSteps.toLocaleString()}</div>
                    <div style={{ fontSize: 'var(--fs-micro)', color: C.text4, fontFamily: "var(--m)", letterSpacing: ".12em" }}>STEPS</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 'var(--fs-h2)', fontWeight: 700, color: C.text2, fontFamily: "var(--m)" }}>{(cardioToday.sessions || []).length}</div>
                    <div style={{ fontSize: 'var(--fs-micro)', color: C.text4, fontFamily: "var(--m)", letterSpacing: ".12em" }}>SESSIONS</div>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 'var(--fs-body)', color: C.text4, fontFamily: "var(--m)", marginTop: 4 }}>
                  Tap to log cardio, steps, or conditioning work
                </div>
              )}
            </Card>
          );
        })()}
      </StaggerItem>
      )}

      {/* ─── MOBILITY (tucked behind MORE) ─── */}
      {showMore && (
      <StaggerItem index={8 + so} visible={visible}>
        {(() => {
          const mobilityData = storage.get("mobility_" + todayKey, {});
          const mobilityDone = Object.keys(mobilityData).length;
          return (
            <Card C={C} onClick={() => onNav?.("mobility")} style={{ padding: 'var(--card-pad)', cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Label C={C} style={{ marginBottom: 0 }}>Mobility</Label>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.text4} strokeWidth="2" strokeLinecap="round"><path d="M9 18l6-6-6-6" /></svg>
              </div>
              {mobilityDone > 0 ? (
                <div style={{ fontSize: 'var(--fs-body)', color: C.accent, fontFamily: "var(--m)", marginTop: 4 }}>
                  {mobilityDone} exercises completed today
                </div>
              ) : (
                <div style={{ fontSize: 'var(--fs-body)', color: C.text4, fontFamily: "var(--m)", marginTop: 4 }}>
                  Stretching, foam rolling, and recovery
                </div>
              )}
            </Card>
          );
        })()}
      </StaggerItem>
      )}

      {/* ─── HYDRATION ─── */}
      <StaggerItem index={9 + so} visible={visible}>
        {trackingLoading ? (
          <Card C={C} style={{ padding: 'var(--card-pad-lg)' }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ height: 14, width: 80, borderRadius: 4, background: C.structBorderHover, animation: "pulse 1.5s ease-in-out infinite" }} />
              <div style={{ height: 18, width: 50, borderRadius: 4, background: C.structBorderHover, animation: "pulse 1.5s ease-in-out infinite" }} />
            </div>
            <div style={{ height: 8, borderRadius: 4, background: C.structBorderHover, marginBottom: 14, animation: "pulse 1.5s ease-in-out infinite" }} />
            <div style={{ height: 40, borderRadius: 8, background: C.structBorderHover, animation: "pulse 1.5s ease-in-out infinite" }} />
          </Card>
        ) : (
        <Card C={C} style={{ padding: 'var(--card-pad-lg)' }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <Label C={C} style={{ marginBottom: 0 }}>Hydration</Label>
            <div style={{ fontSize: 'var(--fs-md)', fontWeight: 700, color: waterCount >= waterGoal ? C.secondary : C.text1, fontFamily: "var(--m)", display: "flex", alignItems: "center", gap: 'var(--gap-sm)' }}>
              {waterCount}/{waterGoal}
              <span style={{ fontSize: 'var(--fs-xs)', color: C.text4, fontWeight: 400 }}>cups</span>
              {waterCount >= waterGoal && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.secondary} strokeWidth="3" strokeLinecap="round"><path d="M20 6L9 17l-5-5" /></svg>
              )}
            </div>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: C.structBorderHover, marginBottom: 14, overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 4,
              width: `${Math.min((waterCount / waterGoal) * 100, 100)}%`,
              background: `linear-gradient(90deg, ${C.secondary}, ${C.secondary}cc)`,
              transition: "width 0.3s ease",
              boxShadow: waterCount > 0 ? `0 0 8px ${C.secondary020}` : "none",
            }} />
          </div>
          <div style={{ display: "flex", gap: 'var(--sp-sm)' }}>
            <button onClick={async () => { tapMedium(); const next = Math.min(waterCount + 1, 20); setWaterCount(next); storage.set("wt_" + todayKey, next); showToast?.(next >= waterGoal ? "Hydration goal hit." : `${next} of ${waterGoal} glasses`); try { await updateTracking({ waterGlasses: next }); } catch (err) { console.error("[Forge] water update failed", err); showToast?.("Failed to save water. Check connection."); } }}
              style={{
                flex: 1, padding: "10px 16px", background: C.structGlass,
                border: `1.5px solid ${C.structBorderHover}`, borderRadius: 8,
                color: C.secondary, fontSize: 'var(--fs-sm)', fontWeight: 700, fontFamily: "var(--m)",
                letterSpacing: ".1em", cursor: "pointer", transition: "all 0.2s",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 'var(--gap-sm)',
              }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              ADD GLASS
            </button>
            {waterCount > 0 && (
              <button onClick={async () => { tapLight(); const next = Math.max(waterCount - 1, 0); setWaterCount(next); storage.set("wt_" + todayKey, next); try { await updateTracking({ waterGlasses: next }); } catch (err) { console.error("[Forge] water update failed", err); showToast?.("Failed to save water. Check connection."); } }}
                style={{
                  padding: "10px 14px", background: C.structGlass,
                  border: `1.5px solid ${C.structBorderHover}`, borderRadius: 8,
                  color: C.text4, fontSize: 'var(--fs-sm)', fontWeight: 700, fontFamily: "var(--m)",
                  cursor: "pointer", transition: "all 0.2s",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>
              </button>
            )}
          </div>
        </Card>
        )}
      </StaggerItem>

      {/* ─── SUPPLEMENTS ─── */}
      <StaggerItem index={10 + so} visible={visible}>
        <Label C={C}>Supplements</Label>
        {(mealsLoading || trackingLoading) ? (
          <Card C={C} style={{ padding: "12px 16px" }}>
            {[1, 2, 3].map(k => (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: k < 3 ? `1px solid ${C.structBorder}` : "none" }}>
                <div style={{ width: 22, height: 22, borderRadius: 6, background: C.structBorderHover, animation: "pulse 1.5s ease-in-out infinite" }} />
                <div style={{ height: 14, width: `${60 + k * 10}%`, borderRadius: 4, background: C.structBorderHover, animation: "pulse 1.5s ease-in-out infinite" }} />
              </div>
            ))}
          </Card>
        ) : (
        <Card C={C} style={{ padding: "2px 16px" }}>
          {SUPPLEMENTS.length === 0 ? (
            <div style={{ fontSize: 'var(--fs-body)', color: C.text4, fontFamily: "var(--m)", textAlign: "center", padding: "12px 0" }}>
              No supplements assigned.
            </div>
          ) : SUPPLEMENTS.map((supp, i) => (
            <div key={i} role="checkbox" aria-checked={!!suppChecked[i]} tabIndex={0} onClick={() => toggleSupp(i)} onKeyDown={e => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), toggleSupp(i))} style={{ display: "flex", alignItems: "center", gap: 'var(--gap-md)', padding: "10px 0", borderBottom: i < SUPPLEMENTS.length - 1 ? `1px solid ${C.structBorder}` : "none", cursor: "pointer" }}>
              <div style={{ width: 22, height: 22, borderRadius: 6, border: `1.5px solid ${suppChecked[i] ? C.secondary : C.structBorderHover}`, background: suppChecked[i] ? C.secondary010 : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.2s" }}>
                {suppChecked[i] && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={C.secondary} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                )}
              </div>
              <div style={{ flex: 1, fontSize: 'var(--fs-md)', color: suppChecked[i] ? C.text3 : C.text2, textDecoration: suppChecked[i] ? "line-through" : "none", transition: "all 0.2s" }}>{supp}</div>
            </div>
          ))}
        </Card>
        )}
      </StaggerItem>

      {/* ─── DOCUMENTS (tucked behind MORE) ─── */}
      {showMore && (
      <StaggerItem index={10 + so} visible={visible}>
        <Card C={C} onClick={() => onNav?.("docs")} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "var(--gap-lg)" }}>
          <div style={{
            width: 40, height: 40, borderRadius: 11,
            background: C.accent010, border: `1.5px solid ${C.accent}25`,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <path d="M14 2v6h6" />
              <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "var(--fs-md)", fontWeight: 600, color: C.text1 }}>Documents</div>
            <div style={{ fontSize: "var(--fs-sm)", color: C.text4, fontFamily: "var(--m)", marginTop: 1 }}>Program PDF, progress reports, workout sheets</div>
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.text4} strokeWidth="2" strokeLinecap="round"><path d="M9 18l6-6-6-6" /></svg>
        </Card>
      </StaggerItem>
      )}

      {/* ─── MORE / LESS toggle — reveals secondary sections in place ─── */}
      <StaggerItem index={10 + so} visible={visible}>
        <button
          onClick={() => { tapLight(); setShowMore(v => !v); }}
          style={{
            width: "100%",
            marginTop: 12, marginBottom: 4,
            padding: "10px 14px",
            background: "transparent",
            border: `1px dashed ${C.structBorderHover}`,
            borderRadius: 10,
            color: C.text3,
            fontSize: "var(--fs-xs)",
            fontFamily: "var(--m)",
            letterSpacing: ".14em",
            textTransform: "uppercase",
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
          <span>{showMore ? "Less" : "More"}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            style={{ transform: showMore ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </StaggerItem>

      <SectionDivider C={C} />

      {/* ─── LIFETIME STATS or WELCOME ─── */}
      <StaggerItem index={11 + so} visible={visible}>
        {stats.workoutCount > 0 ? (
          <Card C={C} style={{ padding: "18px 20px", borderTop: `2px solid ${C.accent020}` }}>
            <div style={{ display: "flex", justifyContent: "space-around" }}>
              {[
                { v: stats.workoutCount, l: "WORKOUTS", color: C.accent },
                { v: stats.streak, l: "STREAK", color: C.secondary },
                { v: stats.checkInCount, l: "CHECK-INS", color: C.accent },
                { v: stats.totalVolumeAllTime > 0 ? `${Math.round(stats.totalVolumeAllTime / 1000)}k` : "—", l: "TOTAL VOL", color: C.secondary },
              ].map(({ v, l, color }) => (
                <div key={l} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 'var(--fs-h2)', fontWeight: 700, color, fontFamily: "var(--m)", textShadow: `0 0 16px ${color}40` }}>{v}</div>
                  <div style={{ fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)", letterSpacing: ".1em", marginTop: 3 }}>{l}</div>
                </div>
              ))}
            </div>
          </Card>
        ) : (
          <Card C={C} style={{ padding: 'var(--sp-2xl)', textAlign: "center", borderTop: `2px solid ${C.secondary020}` }} glow>
            <div style={{
              width: 48, height: 48, borderRadius: 14, margin: "0 auto 14px",
              background: C.secondary010, border: `1.5px solid ${C.secondary}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 0 20px ${C.secondary015}`,
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.secondary} strokeWidth="1.5" strokeLinecap="round">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            </div>
            <div style={{ fontSize: 'var(--fs-h3)', fontWeight: 700, color: C.text1, fontFamily: "var(--d)", marginBottom: 6 }}>Your System Is Ready</div>
            <div style={{ fontSize: 'var(--fs-body)', color: C.text3, fontFamily: "var(--m)", lineHeight: 1.7, maxWidth: 260, margin: "0 auto" }}>
              Complete your first workout. Every session from here compounds — stats, streaks, PR detection, and coaching intelligence all activate the moment you start.
            </div>
          </Card>
        )}
      </StaggerItem>

    </div>
  );
}
